// Password-recovery link handling for /nouveau-mot-de-passe and /reset-password.
// Pure logic (no React, no Supabase import) so it can be tested with a fake auth client.

export const LOGIN_PATH = "/connexion";
export const REQUEST_NEW_LINK_PATH = "/mot-de-passe-oublie";

export type RecoveryLink =
  | { kind: "error"; code: string | null }
  | { kind: "token_hash"; tokenHash: string; type: string }
  | { kind: "implicit"; accessToken: string; refreshToken: string; type: string | null }
  | { kind: "code" }
  | { kind: "none" };

export type RecoveryInvalidReason = "expired" | "invalid" | "missing";

export type RecoveryState =
  | { status: "ready" }
  | { status: "invalid"; reason: RecoveryInvalidReason };

// French-only messages; Supabase's raw English error text is never shown.
export const RECOVERY_MESSAGES: Record<RecoveryInvalidReason, string> = {
  expired:
    "Ce lien de réinitialisation a expiré ou a déjà été utilisé. Demandez un nouveau lien pour choisir votre mot de passe.",
  invalid:
    "Ce lien de réinitialisation n'est pas valide. Demandez un nouveau lien pour choisir votre mot de passe.",
  missing:
    "Aucun lien de réinitialisation n'a été détecté. Ouvrez le lien reçu par courriel ou demandez-en un nouveau.",
};

export const RESET_SUCCESS_MESSAGE = {
  title: "Mot de passe mis à jour",
  description: "Votre mot de passe a été modifié. Connectez-vous avec votre nouveau mot de passe.",
};

type AuthResult<T> = Promise<{ data: T; error: unknown }>;

export interface RecoveryAuth {
  getSession(): AuthResult<{ session: unknown | null }>;
  getUser(): AuthResult<{ user: unknown | null }>;
  setSession(tokens: { access_token: string; refresh_token: string }): AuthResult<{ session: unknown | null }>;
  verifyOtp(params: { token_hash: string; type: "recovery" }): AuthResult<{ session: unknown | null }>;
}

export interface PasswordUpdateAuth {
  updateUser(attrs: { password: string }): AuthResult<unknown>;
  signOut(options: { scope: "local" }): Promise<{ error: unknown }>;
}

/** Reads the recovery parameters from both the query string and the #fragment. */
export function parseRecoveryLink(href: string): RecoveryLink {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { kind: "none" };
  }
  const query = url.searchParams;
  const hash = new URLSearchParams(url.hash.replace(/^#/, ""));
  const get = (key: string) => query.get(key) ?? hash.get(key);

  if (get("error") || get("error_code") || get("error_description")) {
    return { kind: "error", code: get("error_code") ?? get("error") };
  }
  const tokenHash = get("token_hash");
  const type = get("type");
  if (tokenHash && type) return { kind: "token_hash", tokenHash, type };

  const accessToken = hash.get("access_token");
  const refreshToken = hash.get("refresh_token");
  if (accessToken && refreshToken) {
    return { kind: "implicit", accessToken, refreshToken, type: hash.get("type") };
  }
  if (query.get("code")) return { kind: "code" };
  return { kind: "none" };
}

const errorCode = (error: unknown): string | null => {
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === "string" ? code : null;
  }
  return null;
};

const invalid = (reason: RecoveryInvalidReason): RecoveryState => ({ status: "invalid", reason });

/**
 * Establishes (or confirms) the recovery session for the given link.
 * - token_hash + type=recovery → verifyOtp (works in any browser)
 * - implicit #access_token/#refresh_token with type=recovery → setSession
 * - PKCE ?code= → already exchanged automatically by the client on arrival
 * The form is allowed only when a session exists AND the server confirms the user.
 */
export async function resolveRecoverySession(auth: RecoveryAuth, link: RecoveryLink): Promise<RecoveryState> {
  try {
    if (link.kind === "error") {
      return invalid(link.code === "otp_expired" ? "expired" : "invalid");
    }
    if (link.kind === "token_hash") {
      if (link.type !== "recovery") return invalid("invalid");
      const { data, error } = await auth.verifyOtp({ token_hash: link.tokenHash, type: "recovery" });
      if (error || !data?.session) return invalid(errorCode(error) === "otp_expired" ? "expired" : "invalid");
    } else if (link.kind === "implicit") {
      if (link.type !== "recovery") return invalid("invalid");
      const { data, error } = await auth.setSession({
        access_token: link.accessToken,
        refresh_token: link.refreshToken,
      });
      if (error || !data?.session) return invalid("invalid");
    }

    const { data: sessionData } = await auth.getSession();
    if (!sessionData?.session) return invalid(link.kind === "none" ? "missing" : "invalid");

    const { data: userData, error: userError } = await auth.getUser();
    if (userError || !userData?.user) return invalid("invalid");

    return { status: "ready" };
  } catch {
    return invalid("invalid");
  }
}

/** Returns a French validation message, or null when the two passwords are acceptable. */
export function validateNewPassword(password: string, confirmation: string): string | null {
  if (password.length < 8) return "Le mot de passe doit contenir au moins 8 caractères.";
  if (password !== confirmation) return "Les mots de passe ne correspondent pas.";
  return null;
}

const updateErrorMessage = (error: unknown): string => {
  switch (errorCode(error)) {
    case "same_password":
      return "Le nouveau mot de passe doit être différent de l'ancien.";
    case "weak_password":
      return "Ce mot de passe est trop faible. Choisissez-en un plus long ou plus complexe.";
    case "session_not_found":
    case "session_expired":
    case "refresh_token_not_found":
      return "Votre lien de réinitialisation a expiré. Demandez un nouveau lien.";
    default:
      return "Impossible de mettre à jour le mot de passe pour le moment. Réessayez.";
  }
};

export type PasswordResetResult =
  | { ok: true; redirectTo: string }
  | { ok: false; message: string };

/** Saves the new password, then ends the recovery session so the user signs in normally. */
export async function completePasswordReset(auth: PasswordUpdateAuth, password: string): Promise<PasswordResetResult> {
  let updateError: unknown = null;
  try {
    const { error } = await auth.updateUser({ password });
    updateError = error;
  } catch (e) {
    updateError = e ?? new Error("update failed");
  }
  if (updateError) return { ok: false, message: updateErrorMessage(updateError) };

  try {
    await auth.signOut({ scope: "local" });
  } catch {
    // The password is already saved; still send the user to the sign-in page.
  }
  return { ok: true, redirectTo: LOGIN_PATH };
}
